import { memo, useMemo } from 'react';
import { UserSettings } from 'src/types/UserSettings.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { getProductUrl } from 'src/utils/getProductUrl.ts';
import { getExcelFileName } from 'src/utils/getExcelFileName.ts';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { useComposeExcelFile } from 'src/common/hooks/useComposeExcelFile.ts';
import { prepareSensorAttributes } from 'src/sensors/list/prepareSensorAttributes.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { LocationState } from 'src/sensors/LocationState.ts';
import { SensorsAccessoriesPageData } from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';

type Props = {
    userSettings: UserSettings;
    locationState: LocationState;
    isImperial: boolean;
    accessories: SensorsAccessoriesPageData['accessories']['accessories'];
    sensor: SensorsAccessoriesPageData['accessories']['sensor'];
};
export const SensorExportExcel = memo(({
    userSettings,
    locationState,
    isImperial,
    accessories,
    sensor,
}: Props) => {
    const table = useMemo(() => {
        const attr = prepareSensorAttributes(
            sensor,
            userSettings.currentRegion === RegionsEnum.Am,
            isImperial,
            locationState.selectedFlowRange,
        )
            .reduce(((a, v) => [...a, ...v]), []);

        return ({
            columns: [
                MESSAGES.exportExcelMaterial,
                MESSAGES.exportExcelQuantity,
                ...attr.map(([key]) => key),
                MESSAGES.Description,
            ],
            rows: [
                [
                    {
                        text: sensor.Label,
                        hyperlink: getProductUrl({
                            countryDomain: userSettings.countryDomain,
                            currentLocale: userSettings.currentLocale,
                            label: sensor.Label,
                        }),
                    },
                    String(locationState.qty),
                    ...attr.map(([, value]) => value),
                ],
                ...accessories.map((p) => [
                    { text: p.item.Label,
                        hyperlink: getProductUrl({
                            countryDomain: userSettings.countryDomain,
                            currentLocale: userSettings.currentLocale,
                            label: p.item.Label,
                        }) },
                    String(locationState.selectedAccessories
                        .find((accessory) => accessory.label === p.item.Label)?.qty),
                    ...attr.map(() => ''),
                    p.item.description,
                ]),
            ],
        }
        );
    }, [
        accessories,
        isImperial,
        sensor,
        userSettings,
        locationState,
    ]);

    const downloadExcel = useComposeExcelFile({ table });

    return (
        <Button
            as="a"
            href={downloadExcel ?? ''}
            download={getExcelFileName()}
            loading={downloadExcel === null}
            className="vss-checkout-page__navigation-button"
            size="lg"
            kind="tertiary"
            icon={(
                <div className="vss-checkout-page__navigation-icon">
                    <DownloadIcon/>
                </div>
            )}
        >
            <I18n messageId={MESSAGES.exportExcel}/>
        </Button>
    );
});
